<template>
  <b-card>
    <h4 class="mb-2">Ustawienia zwrotów</h4>

    <b-form-group label="Liczba dni na zwrot">
      <b-form-input v-model.number="settings.return_days" type="number" min="1" />
    </b-form-group>

    <b-form-group label="Instrukcja dla klienta">
      <!-- description -->
      <b-form-textarea v-model="settings.client_instruction" rows="5" />
      <b-form-text class="mb-20 text-muted">
       Ta informacja zostanie wyświetlona klientowi po wysłaniu zgłoszenia zwrotu. Możesz tu umieścić instrukcje dotyczące dalszego procesu, takie jak informacje o adresie zwrotu, wymaganych dokumentach czy innych ważnych szczegółach związanych z realizacją zwrotu.
     </b-form-text>
    </b-form-group>

    <b-button variant="primary" class="returns-action-button" :disabled="isSavingSettings" @click="saveSettings">
      <feather-icon icon="CheckIcon" size="14" class="mr-50" />
      <span>{{ isSavingSettings ? 'Zapisywanie...' : 'Zapisz ustawienia' }}</span>
    </b-button>

    <hr class="my-3" />

    <h5 class="mb-2">Powody zwrotu</h5>
    <b-row class="mb-2">
      <b-col md="8">
        <b-form-input v-model="newReason.name" placeholder="Nowy powód zwrotu" />
      </b-col>
      <b-col md="4">
        <b-button variant="primary" class="returns-add-button mt-1 mt-md-0" @click="addReason">
          <feather-icon icon="PlusIcon" size="14" class="mr-50" />
          <span>Dodaj powód</span>
        </b-button>
      </b-col>
    </b-row>

    <b-table responsive bordered striped :items="returnReasons" :fields="reasonFields" :busy="isLoading">
      <template #cell(name)="row">
        <b-form-input v-model="row.item.name" />
      </template>
      <template #cell(is_active)="row">
        <b-form-checkbox v-model="row.item.is_active">Aktywny</b-form-checkbox>
      </template>
      <template #cell(actions)="row">
        <div class="d-flex">
          <b-button variant="primary" size="sm" class="returns-table-button mr-1" @click="saveReason(row.item)">
            <feather-icon icon="CheckIcon" size="13" class="mr-50" />
            <span>Zapisz</span>
          </b-button>
          <b-button variant="danger" size="sm" class="returns-table-button" @click="removeReason(row.item)">Usuń</b-button>
        </div>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';
import { createReason, destroyReason, getSettings, reasons, updateReason, updateSettings } from '@/http/returns';

export default {
  mixins: [NotificationMixin],

  data() {
    return {
      settings: {
        return_days: 14,
        client_instruction: '',
      },
      returnReasons: [],
      newReason: {
        name: '',
        is_active: true,
      },
      reasonFields: [
        { key: 'name', label: 'Nazwa' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: 'Akcje' },
      ],
      isLoading: false,
      isSavingSettings: false,
    };
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      try {
        this.isLoading = true;
        const [settingsResponse, reasonsResponse] = await Promise.all([getSettings(), reasons()]);
        this.settings = settingsResponse.data.settings;
        this.returnReasons = reasonsResponse.data.data;
      } catch (error) {
        this.showErrorNotification('Problem z pobraniem danych', 'Nie udało się pobrać ustawień zwrotów.');
      } finally {
        this.isLoading = false;
      }
    },
    async saveSettings() {
      try {
        this.isSavingSettings = true;
        await updateSettings(this.settings);
        this.showSuccessNotification('Ustawienia zapisane', 'Ustawienia zwrotów zostały zapisane.');
      } catch (error) {
        this.showErrorNotification('Problem z zapisem', 'Nie udało się zapisać ustawień zwrotów.');
      } finally {
        this.isSavingSettings = false;
      }
    },
    async addReason() {
      if (!this.newReason.name) return;
      try {
        await createReason(this.newReason);
        this.newReason.name = '';
        await this.fetchData();
      } catch (error) {
        this.showErrorNotification('Problem z dodaniem', 'Nie udało się dodać powodu zwrotu.');
      }
    },
    async saveReason(reason) {
      try {
        await updateReason(reason.id, { name: reason.name, is_active: reason.is_active });
        this.showSuccessNotification('Powód zapisany', 'Powód zwrotu został zapisany.');
      } catch (error) {
        this.showErrorNotification('Problem z zapisem', 'Nie udało się zapisać powodu zwrotu.');
      }
    },
    async removeReason(reason) {
      try {
        await destroyReason(reason.id);
        await this.fetchData();
      } catch (error) {
        const message = error.response?.data?.message === 'returns.errors.reason_in_use'
          ? 'Nie można usunąć powodu zwrotu, który został już użyty. Dezaktywuj go zamiast usuwać.'
          : 'Nie udało się usunąć powodu zwrotu.';
        this.showErrorNotification('Problem z usunięciem', message);
      }
    },
  },
};
</script>

<style scoped>
.returns-action-button,
.returns-add-button,
.returns-table-button {
  align-items: center;
  display: inline-flex;
  font-weight: 600;
}

.returns-action-button,
.returns-add-button {
  min-height: 31px;
}

.returns-add-button {
  justify-content: center;
  width: auto;
}

@media (max-width: 767.98px) {
  .returns-add-button {
    width: 100%;
  }
}
</style>
